import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MoreActionsMenu } from '../components/ui/MoreActionsMenu'
import {
  getConfirmationCoordination,
  getInformationRequestTask,
  recallConfirmation,
  recallInformationRequest,
  reassignConfirmation,
  reassignInformationRequest,
  withdrawConfirmation,
  withdrawInformationRequest,
} from './assessment-api'
import {
  getApprovalCoordination,
  recallApprovalRequirement,
  reassignApprovalRequirement,
} from './approval-api'
import {
  CustomerSafeRpcError,
  customerSafeOperationMessage,
  toCustomerSafeRpcError,
} from './customer-safe-operation-error'
import type { ActionsReadModelItem } from './governance-api'

type Person = { membershipId: string; displayName: string }

type ManagementCapabilities = {
  sourceType: 'confirmation_requirement' | 'information_request' | 'approval_requirement'
  assignmentRevision: number
  canReassign: boolean
  canRecall: boolean
  canWithdraw: boolean
  eligiblePeople: Person[]
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function objects(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(object) : []
}

function text(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function bool(value: unknown) {
  return value === true
}

function people(value: unknown): Person[] {
  return objects(value)
    .map((person) => ({ membershipId: text(person.membershipId), displayName: text(person.displayName) }))
    .filter((person) => Boolean(person.membershipId && person.displayName))
}

function isManageableSource(action: ActionsReadModelItem): action is ActionsReadModelItem & {
  sourceType: ManagementCapabilities['sourceType']
} {
  return action.sourceType === 'confirmation_requirement'
    || action.sourceType === 'information_request'
    || action.sourceType === 'approval_requirement'
}

function expectedUnavailable(error: unknown) {
  const message = error instanceof Error ? error.message : ''
  return message === 'Confirmation request is not available' || message === 'Approval requirement is not available'
}

function safeOperationError(operation: string, error: unknown) {
  if (error instanceof CustomerSafeRpcError) return error
  return toCustomerSafeRpcError(operation, { message: error instanceof Error ? error.message : '' })
}

async function getManagementCapabilities(action: ActionsReadModelItem): Promise<ManagementCapabilities | null> {
  if (action.sourceType === 'confirmation_requirement') {
    const coordination = await getConfirmationCoordination(action.sourceId)
    return {
      sourceType: action.sourceType,
      assignmentRevision: coordination.assignmentRevision,
      canReassign: coordination.canTransferConfirmation,
      canRecall: coordination.canRecallConfirmation,
      canWithdraw: coordination.canCoordinateConfirmation,
      eligiblePeople: people(coordination.eligibleReviewers),
    }
  }

  if (action.sourceType === 'information_request') {
    const task = await getInformationRequestTask(action.sourceId)
    return {
      sourceType: action.sourceType,
      assignmentRevision: task.capabilities.assignmentRevision,
      canReassign: task.capabilities.canTransfer,
      canRecall: task.capabilities.canRecall,
      canWithdraw: task.capabilities.canWithdraw,
      eligiblePeople: people(task.capabilities.eligibleRecipients),
    }
  }

  if (action.sourceType === 'approval_requirement') {
    const coordination = object(await getApprovalCoordination(action.sourceId))
    return {
      sourceType: action.sourceType,
      assignmentRevision: Number(coordination.assignmentRevision) || 0,
      canReassign: bool(coordination.canReassignApproval),
      canRecall: bool(coordination.canRecallApproval),
      canWithdraw: false,
      eligiblePeople: people(coordination.eligibleApprovers),
    }
  }

  return null
}

function actionLabel(capabilities: ManagementCapabilities) {
  if (capabilities.sourceType === 'confirmation_requirement') return 'specialist'
  if (capabilities.sourceType === 'approval_requirement') return 'approver'
  return 'recipient'
}

function reassignLabel(capabilities: ManagementCapabilities) {
  return capabilities.sourceType === 'information_request' ? 'Reassign recipient' : `Reassign ${actionLabel(capabilities)}`
}

function reassignDescription(capabilities: ManagementCapabilities) {
  if (capabilities.sourceType === 'confirmation_requirement') return 'Move this request to another eligible specialist. Existing history is retained.'
  if (capabilities.sourceType === 'approval_requirement') return 'Move this requirement to another eligible approver. Existing history is retained.'
  return 'Move this request to another eligible recipient. Existing history is retained.'
}

function recallDescription(capabilities: ManagementCapabilities) {
  if (capabilities.sourceType === 'confirmation_requirement') {
    return 'Return the request to the sender before the specialist responds. The same confirmation can be issued again, and existing history is retained.'
  }
  if (capabilities.sourceType === 'approval_requirement') {
    return 'Recall removes the current approver but keeps this deterministic Approval Requirement outstanding. It does not approve, reject, waive or delete it.'
  }
  return 'Recall removes the current recipient but keeps this information request open and blocking.'
}

function withdrawalDescription(capabilities: ManagementCapabilities) {
  if (capabilities.sourceType === 'confirmation_requirement') {
    return 'Close this specialist request because it is no longer required. Existing history is retained. Swell will keep any confirmation that still applies.'
  }
  return 'Withdrawal closes this request without deleting its governed history.'
}

function withdrawalReasons(capabilities: ManagementCapabilities) {
  return capabilities.sourceType === 'confirmation_requirement'
    ? [
        ['assigned_in_error', 'Assigned in error'],
        ['confirmation_no_longer_required', 'Confirmation no longer required'],
        ['assessment_changed', 'Assessment changed'],
        ['duplicate_request', 'Duplicate request'],
        ['other', 'Other'],
      ]
    : [
        ['information_no_longer_required', 'Information no longer required'],
        ['wrong_person', 'Asked the wrong person'],
        ['entered_in_error', 'Request entered in error'],
        ['assessment_changed', 'Assessment changed'],
        ['other', 'Other'],
      ]
}

export function ActionManagementControls({ action }: { action: ActionsReadModelItem }) {
  const queryClient = useQueryClient()
  const [assigneeId, setAssigneeId] = useState('')
  const [transferReason, setTransferReason] = useState('')
  const [recallReason, setRecallReason] = useState('')
  const [withdrawalReason, setWithdrawalReason] = useState('')
  const [withdrawalExplanation, setWithdrawalExplanation] = useState('')
  const [withdrawalConfirmed, setWithdrawalConfirmed] = useState(false)
  const [notice, setNotice] = useState('')
  const [activeAction, setActiveAction] = useState<'reassign' | 'recall' | 'withdraw' | null>(null)
  const manageable = isManageableSource(action) && action.state !== 'completed'

  const capabilitiesQuery = useQuery({
    queryKey: ['r1-actions-management-capabilities', action.sourceType, action.sourceId],
    queryFn: () => getManagementCapabilities(action),
    enabled: manageable,
    retry: false,
  })
  const capabilities = capabilitiesQuery.data

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['r1-actions-read-model'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-my-actions'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-confirmation-task'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-confirmation-coordination'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-information-request-task'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-approval-task'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-approval-coordination'] }),
      queryClient.invalidateQueries({ queryKey: ['r1-actions-management-capabilities', action.sourceType, action.sourceId] }),
    ])
  }

  const reassignMutation = useMutation({
    mutationFn: async () => {
      if (!capabilities) throw new Error('Management capabilities are unavailable')
      try {
        if (capabilities.sourceType === 'confirmation_requirement') {
          return await reassignConfirmation(action.sourceId, capabilities.assignmentRevision, assigneeId, transferReason)
        }
        if (capabilities.sourceType === 'information_request') {
          return await reassignInformationRequest(action.sourceId, capabilities.assignmentRevision, assigneeId, transferReason)
        }
        return await reassignApprovalRequirement(action.sourceId, capabilities.assignmentRevision, assigneeId, transferReason)
      } catch (error) {
        const operation = capabilities.sourceType === 'confirmation_requirement'
          ? 'reassign_r1_confirmation'
          : capabilities.sourceType === 'information_request'
            ? 'reassign_r1_assessment_information_request'
            : 'reassign_r1_approval_requirement'
        throw safeOperationError(operation, error)
      }
    },
    onSuccess: () => {
      setAssigneeId('')
      setTransferReason('')
      setNotice('Assignment updated. Refreshing the source and your Actions list.')
      void refresh()
    },
  })
  const recallMutation = useMutation({
    mutationFn: async () => {
      if (!capabilities) throw new Error('Management capabilities are unavailable')
      try {
        if (capabilities.sourceType === 'confirmation_requirement') {
          return await recallConfirmation(action.sourceId, capabilities.assignmentRevision, recallReason)
        }
        if (capabilities.sourceType === 'information_request') {
          return await recallInformationRequest(action.sourceId, capabilities.assignmentRevision, recallReason)
        }
        return await recallApprovalRequirement(action.sourceId, capabilities.assignmentRevision, recallReason)
      } catch (error) {
        const operation = capabilities.sourceType === 'confirmation_requirement'
          ? 'recall_r1_confirmation'
          : capabilities.sourceType === 'information_request'
            ? 'recall_r1_assessment_information_request'
            : 'recall_r1_approval_requirement'
        throw safeOperationError(operation, error)
      }
    },
    onSuccess: () => {
      setRecallReason('')
      setNotice('Assignment recalled. Refreshing the source and your Actions list.')
      void refresh()
    },
  })
  const withdrawMutation = useMutation({
    mutationFn: async () => {
      if (!capabilities) throw new Error('Management capabilities are unavailable')
      try {
        if (capabilities.sourceType === 'confirmation_requirement') {
          return await withdrawConfirmation(action.sourceId, withdrawalReason, withdrawalExplanation)
        }
        return await withdrawInformationRequest(action.sourceId, withdrawalReason, withdrawalExplanation)
      } catch (error) {
        const operation = capabilities.sourceType === 'confirmation_requirement'
          ? 'cancel_r1_confirmation'
          : 'cancel_r1_assessment_information_request'
        throw safeOperationError(operation, error)
      }
    },
    onSuccess: () => {
      setWithdrawalReason('')
      setWithdrawalExplanation('')
      setWithdrawalConfirmed(false)
      setNotice('Withdrawal recorded. Refreshing the source and your Actions list.')
      void refresh()
    },
  })

  if (!manageable || expectedUnavailable(capabilitiesQuery.error)) return null
  if (capabilitiesQuery.isError) {
    return <div className="actions-management-error" role="alert">Management controls could not be checked. No changes were made.</div>
  }
  if (!capabilities || (!capabilities.canReassign && !capabilities.canRecall && !capabilities.canWithdraw)) return null

  const operationError = reassignMutation.error ?? recallMutation.error ?? withdrawMutation.error
  const withdrawalNeedsExplanation = withdrawalReason === 'other'
  const busy = reassignMutation.isPending || recallMutation.isPending || withdrawMutation.isPending

  const menuActions = [
    capabilities.canReassign ? { id: 'reassign', label: reassignLabel(capabilities), onSelect: () => setActiveAction('reassign' as const) } : null,
    capabilities.canRecall ? { id: 'recall', label: 'Recall assignment', onSelect: () => setActiveAction('recall' as const) } : null,
    capabilities.canWithdraw ? { id: 'withdraw', label: `Withdraw ${capabilities.sourceType === 'confirmation_requirement' ? 'confirmation request' : 'request'}`, danger: true, onSelect: () => setActiveAction('withdraw' as const) } : null,
  ].filter((item): item is NonNullable<typeof item> => Boolean(item))

  return <section className="actions-management" aria-label={`Manage ${action.actionType || action.sourceType}`}>
    <div className="actions-management-menu-row">
      <MoreActionsMenu label={`More actions for ${action.actionType || action.sourceType}`} actions={menuActions} />
    </div>
    {notice ? <div className="actions-management-notice" role="status">{notice}</div> : null}
    {operationError ? <div className="actions-management-error" role="alert">{customerSafeOperationMessage(operationError)}</div> : null}
    <div className="actions-management-controls">
      {capabilities.canReassign ? <details open={activeAction === 'reassign'} hidden={activeAction !== 'reassign'}>
        <summary className="button secondary compact">{reassignLabel(capabilities)}</summary>
        <div className="form-stack">
          <p className="muted">{reassignDescription(capabilities)} This does not answer, approve, complete or alter the governed source.</p>
          <label>New {actionLabel(capabilities)}
            <select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
              <option value="">Choose an eligible active member…</option>
              {capabilities.eligiblePeople
                .filter((person) => person.membershipId !== action.assigneeMembershipId)
                .map((person) => <option key={person.membershipId} value={person.membershipId}>{person.displayName}</option>)}
            </select>
          </label>
          <label>Reason<textarea rows={3} required value={transferReason} onChange={(event) => setTransferReason(event.target.value)} /></label>
          <button className="button secondary" type="button" disabled={!assigneeId || !transferReason.trim() || busy} onClick={() => reassignMutation.mutate()}>
            {reassignMutation.isPending ? 'Reassigning…' : reassignLabel(capabilities)}
          </button>
        </div>
      </details> : null}
      {capabilities.canRecall ? <details open={activeAction === 'recall'} hidden={activeAction !== 'recall'}>
        <summary className="button text compact">Recall assignment</summary>
        <div className="form-stack">
          <p className="muted">{recallDescription(capabilities)}</p>
          <label>Reason<textarea rows={3} required value={recallReason} onChange={(event) => setRecallReason(event.target.value)} /></label>
          <button className="button text" type="button" disabled={!recallReason.trim() || busy} onClick={() => recallMutation.mutate()}>
            {recallMutation.isPending ? 'Recalling…' : 'Recall assignment'}
          </button>
        </div>
      </details> : null}
      {capabilities.canWithdraw ? <details open={activeAction === 'withdraw'} hidden={activeAction !== 'withdraw'}>
        <summary className="button text compact">Withdraw {capabilities.sourceType === 'confirmation_requirement' ? 'confirmation request' : 'request'}</summary>
        <div className="form-stack">
          <p className="muted">{withdrawalDescription(capabilities)}</p>
          <label>Withdrawal reason
            <select value={withdrawalReason} onChange={(event) => setWithdrawalReason(event.target.value)}>
              <option value="">Choose a reason…</option>
              {withdrawalReasons(capabilities).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {withdrawalNeedsExplanation ? <label>Explanation<textarea rows={3} required value={withdrawalExplanation} onChange={(event) => setWithdrawalExplanation(event.target.value)} /></label> : null}
          <label className="actions-withdrawal-confirmation">
            <input type="checkbox" checked={withdrawalConfirmed} onChange={(event) => setWithdrawalConfirmed(event.target.checked)} />
            <span>{capabilities.sourceType === 'confirmation_requirement'
              ? 'I understand Swell will keep a required confirmation in place and will not bypass it.'
              : 'I understand this closes the request while preserving its governed history.'}</span>
          </label>
          <button className="button text" type="button" disabled={!withdrawalReason || (withdrawalNeedsExplanation && !withdrawalExplanation.trim()) || !withdrawalConfirmed || busy} onClick={() => withdrawMutation.mutate()}>
            {withdrawMutation.isPending ? 'Withdrawing…' : 'Confirm withdrawal'}
          </button>
        </div>
      </details> : null}
    </div>
  </section>
}
